import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import unifiedApi from '../../utils/unifiedApi';

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * PhotoUploadPage – anonymous page allowing a user to upload their profile photo
 * via a one-time token included in a reminder email.
 */
const PhotoUploadPage = () => {
  const { token } = useParams();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null); // { firstName, lastName, expiresAt }
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await unifiedApi.getPhotoUploadTokenInfo(token);
        setTokenInfo(data);
      } catch (err) {
        setError(err?.message || 'Invalid or expired photo upload link.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleFileChange = (e) => {
    const inputEl = e.target;
    const file = inputEl.files?.[0];
    // Reset the input value so that re-selecting the same file after a validation
    // or upload error still fires a fresh change event.
    inputEl.value = '';
    if (!file) return;
    setUploadError(null);

    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Please select a valid image file (JPEG/JPG, PNG, GIF, or WebP).');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setUploadError('Image size cannot exceed 10 MB.');
      return;
    }

    // Show local preview before uploading
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);

    handleUpload(file);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    setUploadError(null);
    try {
      const result = await unifiedApi.uploadPhotoViaToken(token, file);
      setUploadedUrl(result?.url);
    } catch (err) {
      setUploadError(err?.message || 'Upload failed. Please try again.');
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, px: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          This link may have expired or already been used. Please log in to your account and
          update your profile photo from your profile page.
        </Typography>
      </Box>
    );
  }

  if (uploadedUrl) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, px: 2, textAlign: 'center' }}>
        <Paper sx={{ p: 4 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Photo uploaded successfully!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Your profile photo has been updated. You are now registered for insurance and
            eligible to play.
          </Typography>
          {preview && (
            <Box
              component="img"
              src={preview}
              alt="Your profile photo"
              sx={{
                width: 160,
                height: 160,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '4px solid',
                borderColor: 'success.main',
                mx: 'auto',
                display: 'block',
              }}
            />
          )}
        </Paper>
      </Box>
    );
  }

  const firstName = tokenInfo?.firstName ?? 'Player';
  const lastName = tokenInfo?.lastName ?? '';

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, px: 2 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom color="error.main">
          ⚠️ Profile Photo Required
        </Typography>

        <Typography variant="body1" sx={{ mb: 2 }}>
          Hi {firstName} {lastName},
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>You are not currently eligible to play.</strong> A profile photo is required
          to register you for insurance. Please upload one now — it only takes a moment.
        </Alert>

        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Photo requirements:
        </Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, color: 'text.secondary', fontSize: 14 }}>
          <li>Headshot or selfie only — just your face and shoulders</li>
          <li>No other people in the photo</li>
          <li>No sunglasses, hats, or other accessories</li>
          <li>Face the camera straight on</li>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          This photo is used solely for insurance identification purposes.
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Accepted formats: JPEG, PNG, GIF, WebP. Maximum size: 10 MB. Your photo will be
          resized automatically.
        </Typography>

        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {uploadError}
          </Alert>
        )}

        {uploading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Uploading…
            </Typography>
          </Box>
        )}

        <Stack direction="row" justifyContent="center">
          <Button
            variant="contained"
            color="error"
            size="large"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Select & Upload Photo
          </Button>
        </Stack>

        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          data-testid="photo-file-input"
        />
      </Paper>
    </Box>
  );
};

export default PhotoUploadPage;
